import React, { useState, useEffect, useCallback } from 'react';
import { useLang } from '../hooks/useLang';
import FörderMatchCard, { SwipeableFörderCards, ConfidenceBadge } from './FörderMatchCard';

const DurumWidget = ({ className = '', compact = false }) => {
  const { lang } = useLang();
  const [durumData, setDurumData] = useState(null);
  const [fundingRecommendations, setFundingRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showFundingRecommendations, setShowFundingRecommendations] = useState(false);

  // Fetch funding recommendations separately if needed
  const fetchFundingRecommendations = useCallback(async () => {
    try {
      const response = await fetch('http://localhost:4000/api/durum/funding-recommendations');
      const data = await response.json();
      
      if (data.success) {
        setFundingRecommendations(data);
        
        // Auto-show if high confidence
        if (data.confidence?.level >= 90) {
          setShowFundingRecommendations(true);
        }
      }
    } catch (err) {
      console.error('Failed to fetch funding recommendations:', err);
      // Use mock recommendations
      setFundingRecommendations(getMockFundingRecommendations());
    }
  }, []);

  // Fetch enhanced durum data with funding recommendations
  const fetchDurumData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Try enhanced widget data first (with funding recommendations)
      let response = await fetch('http://localhost:4000/api/durum/enhanced-widget-data');
      let data = await response.json();
      
      if (data.success && data.fundingRecommendations) {
        // Enhanced data available
        setDurumData(data.data);
        setFundingRecommendations(data.fundingRecommendations);
        setLastUpdated(new Date());
        setError(null);
        
        // Auto-show funding recommendations if high confidence
        if (data.fundingRecommendations.confidence?.level >= 90) {
          setShowFundingRecommendations(true);
        }
      } else {
        // Fallback to basic widget data
        response = await fetch('http://localhost:4000/api/durum/widget-data');
        data = await response.json();
        
        if (data.success) {
          setDurumData(data.data);
          setLastUpdated(new Date());
          setError(null);
          
          // Try to fetch funding recommendations separately
          fetchFundingRecommendations();
        } else {
          setError(data.error || 'Failed to fetch durum data');
        }
      }
    } catch (err) {
      console.error('Durum widget error:', err);
      setError('Connection error');
      // Set mock data for development
      setDurumData(getMockData());
      setFundingRecommendations(getMockFundingRecommendations());
    } finally {
      setLoading(false);
    }
  }, [fetchFundingRecommendations]);

  // (moved above)

  useEffect(() => {
    fetchDurumData();
    // Refresh every 10 minutes
    const interval = setInterval(fetchDurumData, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDurumData]);

  const getMockData = () => ({
    price: {
      current: 285.50,
      change: +2.1,
      trend: 'rising',
      predictions: [
        { date: '2025-08-10', price: 287.30 },
        { date: '2025-08-11', price: 289.80 },
        { date: '2025-08-12', price: 291.15 }
      ]
    },
    signals: {
      buySignal: { signal: 'buy', strength: 72 },
      riskLevel: 'low',
      confidence: 95
    },
    regions: [
      { region: 'italy_puglia', healthStatus: 'Good', healthScore: 82 },
      { region: 'spain_andalucia', healthStatus: 'Excellent', healthScore: 95 },
      { region: 'italy_sicilia', healthStatus: 'Fair', healthScore: 68 }
    ],
    weather: {
      impact: { overall: 'positive' },
      risks: []
    }
  });

  const getMockFundingRecommendations = () => ({
    success: true,
    scenario: {
      type: 'priceRising',
      description: 'Steigende Preise - Lager-Investitionen empfohlen',
      urgency: 'medium'
    },
    confidence: {
      level: 95,
      description: 'Sehr hohe Sicherheit'
    },
    actionRecommendation: {
      confidence: '🔥 SEHR SICHERE Prognose - Jetzt handeln!',
      scenario: 'Steigender Preis → Jetzt in Lagerkapazität investieren!',
      action: 'Investitions-Planung starten'
    },
    matchedPrograms: [
      {
        rank: 1,
        id: 'mock-storage-modernization',
        title: 'Lagermodernisierung & Infrastruktur',
        fundingAmount: { formatted: '60k €', estimated: 60000 },
        effort: { score: 25, level: 'low' },
        matchScore: 95,
        country: 'DE',
        whyRelevant: 'Steigende Durum-Preise (+2.1%) machen Lager-Investitionen hochprofitabel',
        actionAdvice: '🚀 Sofort beantragen - einfach und sehr sicher!',
        trafficLight: { color: '🟢', label: 'Einfach' },
        urgency: 'medium',
        details: {
          carrier: 'Bundesministerium',
          target: ['Farmer'],
          timeline: 'Laufend bis 2025',
          combinable: true
        }
      },
      {
        rank: 2,
        id: 'mock-direct-marketing',
        title: 'Direktvermarktung & Qualitätsstrategie',
        fundingAmount: { formatted: '35k €', estimated: 35000 },
        effort: { score: 40, level: 'medium' },
        matchScore: 85,
        country: 'EU',
        whyRelevant: 'Hohe Preise ermöglichen maximale Direktvermarktungs-Margen',
        actionAdvice: '📋 Zeitnah vorbereiten - Aufwand lohnt sich bei dieser Sicherheit',
        trafficLight: { color: '🟡', label: 'Mittel' },
        urgency: 'medium',
        details: {
          carrier: 'EU-Förderung',
          target: ['Farmer', 'Coop'],
          timeline: 'Beantragung bis März 2025',
          combinable: true
        }
      },
      {
        rank: 3,
        id: 'mock-digital-efficiency',
        title: 'Digitalisierung & Präzisionslandwirtschaft',
        fundingAmount: { formatted: '25k €', estimated: 25000 },
        effort: { score: 55, level: 'medium' },
        matchScore: 75,
        country: 'DE',
        whyRelevant: 'Stabile Marktlage ideal für Effizienz-Investitionen',
        actionAdvice: '📋 Strategisch planen - mittelfristiger Aufwand',
        trafficLight: { color: '🟡', label: 'Mittel' },
        urgency: 'low',
        details: {
          carrier: 'Digitalisierungsförderung',
          target: ['Farmer'],
          timeline: 'Antrag bis Juni 2025',
          combinable: false
        }
      }
    ]
  });

  const formatPrice = (price) => {
    return new Intl.NumberFormat(lang === 'de' ? 'de-DE' : lang === 'es' ? 'es-ES' : 'it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  const getPriceChangeColor = (change) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getTrendEmoji = (trend) => {
    switch (trend) {
      case 'rising': return '📈';
      case 'falling': return '📉';
      default: return '➡️';
    }
  };

  const getSignalColor = (signal) => {
    switch (signal) {
      case 'strong_buy':
      case 'buy':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'sell':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSignalText = (signal) => {
    const signals = {
      strong_buy: { de: 'Starker Kauf', es: 'Compra Fuerte', it: 'Forte Acquisto' },
      buy: { de: 'Kaufen', es: 'Comprar', it: 'Comprare' },
      sell: { de: 'Verkaufen', es: 'Vender', it: 'Vendere' },
      hold: { de: 'Halten', es: 'Mantener', it: 'Mantenere' }
    };
    return signals[signal]?.[lang] || signals[signal]?.de || signal;
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return 'text-green-600';
      case 'medium': return 'text-yellow-600';  
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'Excellent': return 'text-green-600 bg-green-100';
      case 'Good': return 'text-blue-600 bg-blue-100';
      case 'Fair': return 'text-yellow-600 bg-yellow-100';
      case 'Poor': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatRegionName = (region) => {
    const regions = {
      'italy_puglia': { de: 'Apulien, Italien', es: 'Apulia, Italia', it: 'Puglia, Italia' },
      'italy_sicilia': { de: 'Sizilien, Italien', es: 'Sicilia, Italia', it: 'Sicilia, Italia' },
      'spain_andalucia': { de: 'Andalusien, Spanien', es: 'Andalucía, España', it: 'Andalusia, Spagna' },
      'spain_aragon': { de: 'Aragonien, Spanien', es: 'Aragón, España', it: 'Aragona, Spagna' }
    };
    return regions[region]?.[lang] || regions[region]?.de || region;
  };

  // Handler Functions for Funding Recommendations
  const handleDetailsClick = (program) => {
    // Track interaction
    trackRecommendationInteraction(program.id, 'details_clicked');
    
    // Here you could open a modal or navigate to program details
    console.log('Show program details:', program);
    
    // For now, just log the interaction
    alert(`Programm-Details: ${program.title}\nFörderung: ${program.fundingAmount?.formatted || 'Auf Anfrage'}\n\n${program.whyRelevant}`);
  };

  const trackRecommendationInteraction = async (recommendationId, action) => {
    try {
      await fetch('http://localhost:4000/api/durum/track-recommendation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recommendationId,
          action,
          userId: 'anonymous',
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Failed to track interaction:', error);
    }
  };

  const toggleFundingRecommendations = () => {
    setShowFundingRecommendations(!showFundingRecommendations);
    if (!showFundingRecommendations) {
      trackRecommendationInteraction('toggle', 'recommendations_opened');
    }
  };

  // Determine if we should show funding recommendations button
  const shouldShowFundingButton = fundingRecommendations?.matchedPrograms?.length > 0;
  const hasHighConfidenceTrigger = fundingRecommendations?.confidence?.level >= 95 && 
                                   (durumData?.price?.change > 2 || Math.abs(durumData?.price?.change) > 5);

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl shadow-lg border border-amber-200 p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 bg-gray-200 rounded w-32"></div>
            <div className="h-8 bg-gray-200 rounded w-20"></div>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !durumData) {
    return (
      <div className={`bg-white rounded-2xl shadow-lg border border-red-200 p-6 ${className}`}>
        <div className="text-center">
          <div className="text-4xl mb-2">🌾</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {lang === 'de' ? 'Durum-Daten nicht verfügbar' :
             lang === 'es' ? 'Datos de trigo duro no disponibles' :
             'Dati del grano duro non disponibili'}
          </h3>
          <p className="text-gray-600 text-sm mb-4">{error}</p>
          <button 
            onClick={fetchDurumData}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            {lang === 'de' ? 'Erneut versuchen' :
             lang === 'es' ? 'Intentar de nuevo' :
             'Riprova'}
          </button>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl shadow-lg border border-amber-200 p-4 ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <span className="text-xl">🌾</span>
            {lang === 'de' ? 'Durum-Weizen' :
             lang === 'es' ? 'Trigo Duro' :
             'Grano Duro'}
          </h3>
          <span className="text-xs text-gray-500">Live</span>
        </div>
        
        <div className="flex items-center justify-between mb-2">
          <div className="text-2xl font-bold text-gray-900">
            {formatPrice(durumData?.price?.current || 0)}
            <span className="text-sm text-gray-500 ml-1">/t</span>
          </div>
          <div className={`text-sm font-semibold ${getPriceChangeColor(durumData?.price?.change || 0)}`}>
            {durumData?.price?.change > 0 ? '+' : ''}{durumData?.price?.change?.toFixed(1) || 0}%
            <span className="ml-1">{getTrendEmoji(durumData?.price?.trend)}</span>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className={`px-2 py-1 rounded-full border ${getSignalColor(durumData?.signals?.buySignal?.signal)}`}>
            {getSignalText(durumData?.signals?.buySignal?.signal)}
          </span>
          <span className={`${getRiskColor(durumData?.signals?.riskLevel)} font-semibold`}>
            Risk: {durumData?.signals?.riskLevel || 'low'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl shadow-lg border border-amber-200 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <span className="text-2xl">🌾</span>
          {lang === 'de' ? 'Durum-Weizen Markt' :
           lang === 'es' ? 'Mercado de Trigo Duro' :
           'Mercato del Grano Duro'}
        </h3>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs text-gray-500">Live</span>
        </div>
      </div>

      {/* Current Price */}
      <div className="bg-white rounded-xl p-4 mb-6 border border-amber-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">
            {lang === 'de' ? 'Aktueller Preis' :
             lang === 'es' ? 'Precio Actual' :
             'Prezzo Attuale'}
          </span>
          <span className="text-xs text-gray-500">EUR/Tonne</span>
        </div>
        <div className="flex items-baseline justify-between">
          <div className="text-3xl font-bold text-gray-900">
            {formatPrice(durumData?.price?.current || 0)}
          </div>
          <div className="text-right">
            <div className={`text-lg font-semibold ${getPriceChangeColor(durumData?.price?.change || 0)}`}>
              {durumData?.price?.change > 0 ? '+' : ''}{durumData?.price?.change?.toFixed(1) || 0}%
            </div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              {getTrendEmoji(durumData?.price?.trend)}
              {lang === 'de' ? 'Trend' : lang === 'es' ? 'Tendencia' : 'Tendenza'}
            </div>
          </div>
        </div>
      </div>

      {/* Market Signals */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-amber-100">
          <div className="text-sm font-medium text-gray-600 mb-2">
            {lang === 'de' ? 'Signal' : lang === 'es' ? 'Señal' : 'Segnale'}
          </div>
          <div className={`inline-flex px-3 py-1 rounded-full border text-sm font-semibold ${getSignalColor(durumData?.signals?.buySignal?.signal)}`}>
            {getSignalText(durumData?.signals?.buySignal?.signal)}
          </div>
          <div className="text-xs text-gray-500 mt-2">
            {lang === 'de' ? 'Stärke' : lang === 'es' ? 'Fuerza' : 'Forza'}: {durumData?.signals?.buySignal?.strength || 0}%
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-amber-100">
          <div className="text-sm font-medium text-gray-600 mb-2">
            {lang === 'de' ? 'Risiko' : lang === 'es' ? 'Riesgo' : 'Rischio'}
          </div>
          <div className={`text-lg font-bold ${getRiskColor(durumData?.signals?.riskLevel)}`}>
            {durumData?.signals?.riskLevel === 'low' ? 
              (lang === 'de' ? 'Niedrig' : lang === 'es' ? 'Bajo' : 'Basso') :
             durumData?.signals?.riskLevel === 'medium' ?
              (lang === 'de' ? 'Mittel' : lang === 'es' ? 'Medio' : 'Medio') :
              (lang === 'de' ? 'Hoch' : lang === 'es' ? 'Alto' : 'Alto')
            }
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {lang === 'de' ? 'Vertrauen' : lang === 'es' ? 'Confianza' : 'Fiducia'}: {durumData?.signals?.confidence || 0}%
          </div>
        </div>
      </div>

      {/* Predictions */}
      {durumData?.price?.predictions && durumData.price.predictions.length > 0 && (
        <div className="bg-white rounded-xl p-4 mb-6 border border-amber-100">
          <div className="text-sm font-medium text-gray-600 mb-3">
            {lang === 'de' ? 'Prognose (nächste Tage)' :
             lang === 'es' ? 'Pronóstico (próximos días)' :
             'Previsione (prossimi giorni)'}
          </div>
          <div className="space-y-2">
            {durumData.price.predictions.slice(0, 3).map((pred, index) => (
              <div key={index} className="flex justify-between items-center text-sm">
                <span className="text-gray-600">
                  {new Date(pred.date).toLocaleDateString(lang === 'de' ? 'de-DE' : lang === 'es' ? 'es-ES' : 'it-IT')}
                </span>
                <span className="font-semibold">
                  {formatPrice(pred.price)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regional Health */}
      {durumData?.regions && durumData.regions.length > 0 && (
        <div className="bg-white rounded-xl p-4 border border-amber-100">
          <div className="text-sm font-medium text-gray-600 mb-3">
            {lang === 'de' ? 'Anbauregionen' :
             lang === 'es' ? 'Regiones de Cultivo' :
             'Regioni di Coltivazione'}
          </div>
          <div className="space-y-2">
            {durumData.regions.slice(0, 3).map((region, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-sm text-gray-700">
                  {formatRegionName(region.region)}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getHealthStatusColor(region.healthStatus)}`}>
                  {region.healthStatus} ({region.healthScore}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* High-Confidence Market Alert */}
      {hasHighConfidenceTrigger && fundingRecommendations?.actionRecommendation && (
        <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white p-4 rounded-xl mb-4 animate-pulse">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">🚨</span>
            <div className="font-bold">
              {fundingRecommendations.actionRecommendation.confidence}
            </div>
          </div>
          <div className="text-sm mb-3">
            {fundingRecommendations.actionRecommendation.scenario}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs opacity-90">
              Basiert auf 1396 Marktdaten
            </span>
            <button 
              onClick={toggleFundingRecommendations}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded-lg text-xs font-medium transition-all"
            >
              {showFundingRecommendations ? 'Weniger zeigen' : 'Programme ansehen'}
            </button>
          </div>
        </div>
      )}

      {/* Funding Recommendations Button */}
      {shouldShowFundingButton && !hasHighConfidenceTrigger && (
        <div className="mb-4">
          <button
            onClick={toggleFundingRecommendations}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-xl font-medium transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <span className="text-lg">🎯</span>
            {showFundingRecommendations ? 
              'Förder-Empfehlungen ausblenden' : 
              `${fundingRecommendations?.matchedPrograms?.length || 0} passende Programme finden`
            }
            <span className="bg-white bg-opacity-20 px-2 py-1 rounded-full text-xs">
              {fundingRecommendations?.confidence?.level || 95}% sicher
            </span>
          </button>
        </div>
      )}

      {/* Funding Recommendations Section */}
      {showFundingRecommendations && fundingRecommendations && (
        <div className="mb-6 space-y-4">
          {/* Confidence Badge */}
          <ConfidenceBadge 
            confidence={fundingRecommendations.confidence?.level || 95}
            scenario={fundingRecommendations.scenario?.description || 'Marktanalyse'}
          />

          {/* Market Scenario Explanation */}
          <div className="bg-white p-4 rounded-xl border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📊</span>
              <span className="font-semibold text-gray-800">Aktuelle Marktlage</span>
            </div>
            <div className="text-sm text-gray-700 mb-3">
              {fundingRecommendations.scenario?.description}
            </div>
            <div className="text-sm font-medium text-blue-700">
              💡 {fundingRecommendations.actionRecommendation?.action}
            </div>
          </div>

          {/* Program Recommendations */}
          {fundingRecommendations.matchedPrograms?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span>🎯</span>
                  Empfohlene Programme
                </h4>
                <span className="text-sm text-gray-600">
                  Top {fundingRecommendations.matchedPrograms.length}
                </span>
              </div>

              {/* Desktop: Show first 3 as cards */}
              <div className="hidden md:block space-y-4">
                {fundingRecommendations.matchedPrograms.slice(0, 3).map((program) => (
                  <FörderMatchCard
                    key={program.id}
                    program={program}
                    onDetailsClick={handleDetailsClick}
                    onTrackInteraction={trackRecommendationInteraction}
                    compact={true}
                  />
                ))}
              </div>

              {/* Mobile: Swipeable cards */}
              <div className="md:hidden">
                <SwipeableFörderCards
                  programs={fundingRecommendations.matchedPrograms.slice(0, 3)}
                  onDetailsClick={handleDetailsClick}
                  onTrackInteraction={trackRecommendationInteraction}
                />
              </div>

              {/* Show More Button */}
              {fundingRecommendations.matchedPrograms.length > 3 && (
                <div className="text-center mt-4">
                  <button 
                    onClick={() => trackRecommendationInteraction('show_more', 'clicked')}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    + {fundingRecommendations.matchedPrograms.length - 3} weitere Programme anzeigen
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Data Source Badge */}
          <div className="text-center">
            <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Basiert auf {fundingRecommendations.totalAvailablePrograms || 555} Programmen
            </span>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-xs text-gray-500 text-center mt-4">
        {lang === 'de' ? 'Letztes Update' :
         lang === 'es' ? 'Última actualización' :
         'Ultimo aggiornamento'}: {lastUpdated?.toLocaleTimeString()}
        {error && (
          <span className="ml-2 text-amber-600">
            ({lang === 'de' ? 'Testdaten' : lang === 'es' ? 'Datos de prueba' : 'Dati di test'})
          </span>
        )}
      </div>
    </div>
  );
};

export default DurumWidget;
